import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import TomSelect from "tom-select"
import { allProjectsStore } from "../../stores/all_projects_store"
import { allUsersStore } from "../../stores/organization/all_users_store"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { getCsrfToken } from "shared/form_helpers";

export default class extends Controller {

    static stores = [
        allProjectsStore,
        allUsersStore
    ]

    static targets = [
        'form',
        'submitButton',
        'emailInput',
        'projectSelect'
    ]

    connect() {
        useStore(this)

        // Turbo Drive keeps allProjectsStore (a module-level singleton) across navigations, so
        // it may still hold a project list written by a previous page (e.g. member-scoped or
        // from another session). Clear it before rendering; the page's projects supplier then
        // fills it with every org project (org-scope token) after its async fetch completes.
        this.setAllProjectsStoreValue([])

        // Enhance the project multi-select with TomSelect and gate the submit button
        // until at least one Project is selected (Req 18.4), mirroring the server-side guard.
        // Options come from allProjectsStore (see _renderProjectOptions).
        if (this.hasProjectSelectTarget) {
            this.projectTomSelect = new TomSelect(this.projectSelectTarget, {
                plugins: ['remove_button'],
                placeholder: 'Select one or more projects',
                create: false,
                maxItems: null,
                options: [],
                onChange: () => this.updateSubmitState()
            })
        }

        this._renderProjectOptions(this.allProjectsStoreValue)
        this.updateSubmitState()
    }

    disconnect() {
        if (this.projectTomSelect) {
            this.projectTomSelect.destroy()
            this.projectTomSelect = null
        }
    }

    // Repopulate the project select whenever the projects supplier loads the org's projects.
    onAllProjectsStoreUpdate(projects) {
        this._renderProjectOptions(projects)
    }

    // Disable the submit button until at least one Project is selected.
    updateSubmitState() {
        if (!this.hasSubmitButtonTarget) {
            return
        }

        this.submitButtonTarget.disabled = this.selectedProjectIds().length === 0
    }

    // Returns the currently selected Project ids.
    selectedProjectIds() {
        if (this.projectTomSelect) {
            const value = this.projectTomSelect.getValue()
            return Array.isArray(value) ? value : [value].filter(Boolean)
        }

        if (this.hasProjectSelectTarget) {
            return Array.from(this.projectSelectTarget.selectedOptions).map(opt => opt.value)
        }

        return []
    }

    async submit(event) {
        event.preventDefault()

        // Guard: at least one Project must be selected (mirrors the server-side guard).
        if (this.selectedProjectIds().length === 0) {
            sendFailureNotification('Please select at least one project')
            return
        }

        // Disable submit button to prevent double submission
        this.submitButtonTarget.disabled = true
        const originalText = this.submitButtonTarget.textContent
        this.submitButtonTarget.textContent = "Inviting..."

        try {
            const formData = new FormData(this.formTarget)
            const response = await fetch(this.formTarget.action, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-Token': getCsrfToken(),
                    'Accept': 'application/json'
                }
            })

            const responseBody = await response.json()

            if (response.ok) {
                // Success - show a success message and clear form
                sendSuccessNotification('Members invited successfully')
                this.emailInputTarget.value = ''
                if (this.projectTomSelect) {
                    this.projectTomSelect.clear()
                }

                // Add newly invited users to the allUsersStore
                // This will automatically update the users table
                if (responseBody.data.users && Array.isArray(responseBody.data.users)) {
                    const currentUsers = this.allUsersStoreValue || []
                    const updatedUsers = [...currentUsers, ...responseBody.data.users]
                    this.setAllUsersStoreValue(updatedUsers)
                }
            } else {
                // Error - show an error message
                sendFailureNotification(responseBody.message || 'Failed to invite members')
            }
        } catch (error) {
            console.error('Error inviting users:', error)
            sendFailureNotification('An error occurred while inviting members. Please try again.')
        } finally {
            // Re-enable submit button
            this.submitButtonTarget.textContent = originalText
            this.updateSubmitState()
        }
    }

    /**
     * Populate the project multi-select from allProjectsStore (sorted by name), preserving the
     * current selection. Mirrors projects_management_controller.js#_renderCreateMemberOptions.
     */
    _renderProjectOptions(projectsValue) {
        // useStore can fire the store callback before the TomSelect is created.
        if (!this.projectTomSelect) return

        const projects = Array.isArray(projectsValue) ? [...projectsValue] : []
        projects.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))

        const selected = this.projectTomSelect.getValue()
        this.projectTomSelect.clearOptions()

        let order = 0
        projects.forEach((project) => {
            if (!project || !project.id) return
            this.projectTomSelect.addOption({
                value: project.id,
                text: project.name,
                $order: order++
            })
        })

        // If the user already focused the (still empty) select while the supplier was loading,
        // open the dropdown now that options exist; otherwise just refresh silently.
        this.projectTomSelect.refreshOptions(this.projectTomSelect.isFocused)
        if (selected && selected.length) {
            this.projectTomSelect.setValue(selected, true)
        }
        this.updateSubmitState()
    }
}
